import { z } from "zod";
const identifier = z.string().trim().min(1).max(300);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
const authoritySchema = z.object({
    tenantSlug: identifier.max(120),
    runId: identifier,
    workerId: identifier,
    attemptNumber: z.number().int().positive(),
    pluginSlug: identifier.max(120),
    toolName: identifier.max(240),
    toolCallId: identifier,
    requestSha256: sha256,
}).strict();
export const pluginWriteIntentPrepareRequestSchema = authoritySchema.extend({
    operation: z.literal("prepare"),
}).strict();
export const pluginWriteReceiptOutcomeSchema = z.enum([
    "succeeded",
    "failed",
    "outcome_unknown",
]);
export const pluginWriteIntentCompleteRequestSchema = authoritySchema.extend({
    operation: z.literal("complete"),
    intentId: identifier,
    outcome: pluginWriteReceiptOutcomeSchema,
    resultSha256: sha256.nullable(),
    failureClass: z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,119}$/u).nullable(),
}).strict().superRefine((value, context) => {
    if ((value.outcome === "succeeded") !== (value.resultSha256 !== null)) {
        context.addIssue({ code: "custom", path: ["resultSha256"],
            message: "resultSha256 is required only for succeeded writes" });
    }
    if ((value.outcome !== "succeeded") !== (value.failureClass !== null)) {
        context.addIssue({ code: "custom", path: ["failureClass"],
            message: "failureClass is required only for non-succeeded writes" });
    }
});
export const pluginWriteIntentRequestSchema = z.union([
    pluginWriteIntentPrepareRequestSchema,
    pluginWriteIntentCompleteRequestSchema,
]);
const receiptSchema = z.object({
    id: identifier,
    outcome: pluginWriteReceiptOutcomeSchema,
    resultSha256: sha256.nullable(),
    failureClass: z.string().nullable(),
    createdAt: z.number().int().positive().safe(),
}).strict();
export const pluginWriteIntentResponseSchema = z.union([
    z.object({ disposition: z.literal("execute"), intentId: identifier, toolCallId: identifier.optional() }).strict(),
    z.object({ disposition: z.enum(["succeeded", "blocked"]), intentId: identifier,
        receipt: receiptSchema.nullable() }).strict(),
    z.object({ disposition: z.enum(["recorded", "replayed"]), intentId: identifier,
        receipt: receiptSchema }).strict(),
]);
export function canonicalPluginWriteJson(value) {
    if (value === null || typeof value === "boolean" || typeof value === "string") {
        return JSON.stringify(value);
    }
    if (typeof value === "number" && Number.isFinite(value))
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalPluginWriteJson).join(",")}]`;
    if (value && typeof value === "object") {
        return `{${Object.entries(value)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([key, child]) => `${JSON.stringify(key)}:${canonicalPluginWriteJson(child)}`)
            .join(",")}}`;
    }
    throw new Error("Plugin write hashing accepts JSON values only.");
}
